/**
 * EditorGuide — 가이드 맵 생성(에디터 단계). 셋업 페이지(이름/층/IP/확인)에
 * 이어 DXF 업로드 → 레이어-객체 매핑 → 조명 그룹핑 → PDF 매핑(CCTV IP·이름·
 * 구역·동) → 관계 생성(반경) → 저장 → 마무리(뷰어/속성 시트 안내)까지, 프로젝트
 * 생성부터 맵 렌더링까지 전 구간을 셋업 페이지와 동일한 풀페이지 위저드 카드로
 * 이어 보여준다(GuideProgress 로 "N/11 단계" 공통 진행 표시).
 *
 * 화면 구성:
 *  · DXF 업로드 — 파일은 항상 사용자가 직접 고른다(자동 선택 없음). 서버 DXF
 *    목록이 뜨면(파일이 있어 고르거나, 없어서 업로드 버튼만 있거나) 카드가
 *    자동으로 비켜 목록/업로드 화면을 드러낸다(csvStore 의 dxfListReady 신호로
 *    관찰). 레이어 선택 모달이 뜰 때도 마찬가지로 비켜준다(dxfLayerModalOpen).
 *    카드의 버튼을 직접 눌러 먼저 비킬 수도 있다. 실제 업로드/파싱 로직은
 *    그대로 캔버스 쪽 컴포넌트가 수행하며, 맵이 렌더링되면(레이어 매핑 모달이
 *    뜨면) 다음 단계로 자동 전환된다.
 *  · 레이어 매핑 — 진입 조건 자체가 "매핑 모달이 이미 열려 있음"이라 카드가
 *    뜰 틈이 없다(항상 즉시 비켜줌) — 그래서 소형 배너로만 진행 표시를 남긴다.
 *  · DXF·레이어 매핑 이후(조명 그룹핑부터) — 카드의 액션 버튼이 기존 모달을
 *    열면 카드는 숨고, 모달 confirm(통지형 completeStep) 또는 상태 관찰로
 *    다음 단계 카드가 뜬다.
 *
 * 가이드는 화면에서 절대 완전히 사라지지 않는다 — 풀페이지 카드가 비켜야
 * 하는 모든 상황(파일 선택·모달·시트·진행 오버레이)에서는 상단 소형 배너로
 * 축소해 현재 단계와 진행 표시를 계속 보여준다. 예전처럼 null 을 반환해
 * 통째로 숨어버리면 사용자는 "가이드가 끝났다"고 오해한다(레이어 매핑 이후
 * 단계가 안 이어지는 것처럼 보이는 문제).
 *
 * 기존 로직을 그대로 쓴다 — 이 컴포넌트는 어떤 단계도 직접 수행하지 않는다.
 */
import React, { useEffect, useRef, useState } from 'react'
import {
  EDITOR_GUIDE_STEP_ORDER,
  type EditorGuideStep,
  useGuidedMapStore,
} from '../model/guidedMapStore'
import { useCsvRuntimeStore } from '../../csv/model/csvStore'
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore'
import { GuideProgress } from './GuideProgress'
import { PdfPickerModal } from '../../cctv-zone-mapping/ui/PdfPickerModal'
import styles from './EditorGuide.module.css'

interface EditorGuideProps {
  /** 레이어-객체 매핑 모달(LayerMappingModal) 열림 여부. */
  mappingModalOpen: boolean
  /** 캔버스에 렌더링된 객체 수. */
  elementCount: number
  /** CCTV & 구역 매핑 파이프라인 단계('idle' 외 = 진행 중). */
  cctvMappingStep: string
  /** 조명 자동 그룹핑 모달 열림 여부(열려 있는 동안 카드 숨김). */
  lightsModalOpen: boolean
  /** 자동 관계 생성 모달 열림 여부(열려 있는 동안 카드 숨김). */
  relationsModalOpen: boolean
  /** 객체 속성 시트(하단 도킹) 열림 여부(열려 있는 동안 카드 숨김). */
  propertySheetOpen: boolean
  /** 저장 성공 모달 표시 여부(저장 완료 신호 + 표시 중 카드 숨김). */
  saveSuccess: boolean
  isSaving: boolean
  onOpenAutoGroupLights: () => void
  onPdfSelected: (file: File) => void
  onOpenAutoLink: () => void
  onSave: () => void
  onOpenPropertySheet: () => void
  onOpenViewer: () => void
}

const STEP_TITLES: Record<EditorGuideStep, string> = {
  dxf: '도면(DXF)을 업로드해 주세요',
  layerMap: '레이어-객체 타입 매핑을 확인해 주세요',
  lights: '조명을 그룹핑해 주세요',
  pdf: '도면 PDF 로 CCTV·구역·동을 매핑해 주세요',
  relations: '객체 간 관계를 생성해 주세요',
  save: '맵을 저장해 주세요',
  done: '🎉 맵 생성이 모두 끝났습니다!',
}

const STEP_MESSAGES: Record<EditorGuideStep, string> = {
  dxf:
    '아래 버튼을 누르면 서버 DXF 파일 목록이 뜹니다. 목록에서 파일을 골라도 되고, 새 파일을 업로드해도 됩니다.',
  layerMap:
    '자동 매핑 결과를 확인하고 필요한 부분만 고친 뒤 확인을 누르면 맵이 렌더링됩니다.',
  lights:
    '그룹핑 반경(임계값)을 조정하면 묶일 조명이 실시간 미리보기로 표시됩니다. 조정 후 진행을 누르세요.',
  pdf:
    '도면 PDF 하나로 CCTV IP → CCTV 이름 → 구역 → 동을 순서대로 자동 매핑합니다. 서버에 저장된 PDF를 목록에서 선택하거나, 없으면 새로 업로드하세요.',
  relations:
    '관계별 반경을 조정하고 적용하세요. 적용하면 화면 하단에 속성 시트가 열려 결과를 바로 확인/수정할 수 있어요.',
  save:
    '저장하면 맵이 렌더링된 상태로 서버에 저장됩니다.',
  done:
    '뷰어로 확인하거나(EXPORT 가능), 속성 시트로 값을 수정하거나, 이대로 에디터에서 계속 편집할 수 있어요.',
}

/**
 * 풀페이지 위저드 카드로 표시하는 단계. 'layerMap' 만 예외 — 진입 조건 자체가
 * "매핑 모달이 이미 열려 있음"이라 카드가 뜰 틈이 없어(항상 즉시 비켜줌) 굳이
 * 포함해도 화면에 보이지 않는다. 그래서 소형 배너로만 남긴다.
 */
const FULL_PAGE_STEPS: EditorGuideStep[] = ['dxf', 'lights', 'pdf', 'relations', 'save', 'done']

export const EditorGuide: React.FC<EditorGuideProps> = ({
  mappingModalOpen,
  elementCount,
  cctvMappingStep,
  lightsModalOpen,
  relationsModalOpen,
  propertySheetOpen,
  saveSuccess,
  isSaving,
  onOpenAutoGroupLights,
  onPdfSelected,
  onOpenAutoLink,
  onSave,
  onOpenPropertySheet,
  onOpenViewer,
}) => {
  const step = useGuidedMapStore(s => s.step)
  const projectName = useGuidedMapStore(s => s.projectName)
  const advance = useGuidedMapStore(s => s.advance)
  const end = useGuidedMapStore(s => s.end)
  // PDF 파이프라인이 한 번이라도 돌기 시작했는지(시작→idle 복귀 = 완료).
  const pdfStartedRef = useRef(false)
  const siteProjectId = useObjectTypeStore(s => s.siteProjectId)
  const [pdfPickerOpen, setPdfPickerOpen] = useState(false)

  // DXF 카드 자동 비켜주기 — 서버 목록이 뜨거나(파일 선택은 항상 사용자가
  // 직접 함) 레이어 선택 모달이 뜨면(둘 다 csvStore 신호) 버튼을 누르지
  // 않아도 카드가 물러난다.
  const [dxfCardDismissed, setDxfCardDismissed] = useState(false)
  const dxfListReady = useCsvRuntimeStore(s => s.dxfListReady)
  const dxfLayerModalOpen = useCsvRuntimeStore(s => s.dxfLayerModalOpen)

  // 'dxf' 를 벗어나면 래치를 초기화 — 다른 프로젝트에서 다시 'dxf' 로 들어올 때 카드가 뜨도록.
  useEffect(() => {
    if (step !== 'dxf') setDxfCardDismissed(false)
  }, [step])

  useEffect(() => {
    if (step === 'dxf' && (dxfListReady || dxfLayerModalOpen)) setDxfCardDismissed(true)
  }, [step, dxfListReady, dxfLayerModalOpen])

  // 관찰형 전이 -------------------------------------------------------------
  useEffect(() => {
    if (step === 'dxf' && mappingModalOpen) advance('layerMap')
  }, [step, mappingModalOpen, advance])

  useEffect(() => {
    if (step === 'layerMap' && !mappingModalOpen && elementCount > 0) advance('lights')
  }, [step, mappingModalOpen, elementCount, advance])

  useEffect(() => {
    if (step !== 'pdf') return
    if (cctvMappingStep !== 'idle') {
      pdfStartedRef.current = true
      return
    }
    if (pdfStartedRef.current) advance('relations')
  }, [step, cctvMappingStep, advance])

  useEffect(() => {
    if (step === 'save' && saveSuccess) advance('done')
  }, [step, saveSuccess, advance])

  // 이미 객체가 렌더링된 맵(재진입)이라면 DXF 단계는 건너뛴다.
  useEffect(() => {
    if (step === 'dxf' && elementCount > 0 && !mappingModalOpen) advance('lights')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, elementCount])

  const stepIdx = EDITOR_GUIDE_STEP_ORDER.indexOf(step)

  const skip = () => {
    const next = EDITOR_GUIDE_STEP_ORDER[stepIdx + 1]
    if (next) advance(next)
  }

  // 사용자가 기존 모달/시트/진행 오버레이와 상호작용하는 동안 카드는 비켜준다.
  const interacting =
    lightsModalOpen ||
    relationsModalOpen ||
    propertySheetOpen ||
    isSaving ||
    saveSuccess ||
    cctvMappingStep !== 'idle'

  // 풀페이지 카드 표시 조건 — 그 외(레이어 매핑, 파일 선택, 모달/시트/진행
  // 오버레이와 상호작용 중)에는 아래 상단 소형 배너로 축소해 항상 보인다.
  const showFullCard =
    FULL_PAGE_STEPS.includes(step) &&
    !interacting &&
    !(step === 'dxf' && dxfCardDismissed)

  if (showFullCard) {
    return (
      <>
        {pdfPickerOpen && siteProjectId != null && (
          <PdfPickerModal
            siteProjectId={siteProjectId}
            onFileReady={file => {
              setPdfPickerOpen(false)
              onPdfSelected(file)
            }}
            onCancel={() => setPdfPickerOpen(false)}
          />
        )}
        <div className={styles.fullOverlay} role="dialog" aria-label="가이드 맵 생성">
        <div key={step} className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.cardProject}>
              🧭 가이드 맵 생성{projectName ? ` — ${projectName}` : ''}
            </span>
            <button type="button" className={styles.closeButton} onClick={end} title="가이드 종료">
              ✕
            </button>
          </div>

          <GuideProgress current={step} />

          <h1 className={styles.title}>{STEP_TITLES[step]}</h1>
          <p className={styles.description}>{STEP_MESSAGES[step]}</p>

          <div className={styles.buttons}>
            {step === 'dxf' && (
              <>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => setDxfCardDismissed(true)}
                >
                  📄 DXF 파일 선택
                </button>
                <button type="button" className={styles.secondaryButton} onClick={skip}>
                  건너뛰기
                </button>
              </>
            )}

            {step === 'lights' && (
              <>
                <button type="button" className={styles.primaryButton} onClick={onOpenAutoGroupLights}>
                  💡 조명 그룹핑 열기
                </button>
                <button type="button" className={styles.secondaryButton} onClick={skip}>
                  건너뛰기
                </button>
              </>
            )}

            {step === 'pdf' && (
              <>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => setPdfPickerOpen(true)}
                  disabled={siteProjectId == null}
                >
                  📄 도면 PDF 선택
                </button>
                <button type="button" className={styles.secondaryButton} onClick={skip}>
                  건너뛰기
                </button>
              </>
            )}

            {step === 'relations' && (
              <>
                <button type="button" className={styles.primaryButton} onClick={onOpenAutoLink}>
                  🔗 관계 생성 열기
                </button>
                <button type="button" className={styles.secondaryButton} onClick={skip}>
                  건너뛰기
                </button>
              </>
            )}

            {step === 'save' && (
              <button type="button" className={styles.primaryButton} onClick={onSave}>
                💾 저장하기
              </button>
            )}

            {step === 'done' && (
              <>
                <button type="button" className={styles.primaryButton} onClick={onOpenViewer}>
                  🗺️ 뷰어 열기
                </button>
                <button type="button" className={styles.secondaryButton} onClick={onOpenPropertySheet}>
                  📋 속성 시트
                </button>
                <button type="button" className={styles.secondaryButton} onClick={end}>
                  가이드 종료
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      </>
    )
  }

  // 상단 소형 배너 — 풀페이지 카드가 비켜 있는 동안(레이어 매핑, DXF 파일
  // 선택, 각 단계 모달/시트/진행 중)에도 가이드가 끊긴 것처럼 보이지 않게
  // 현재 단계와 진행 표시를 계속 유지한다.
  return (
    <div className={styles.banner} role="region" aria-label="가이드 맵 생성">
      <div className={styles.headerRow}>
        <span className={styles.bannerTitle}>
          🧭 가이드 맵 생성{projectName ? ` — ${projectName}` : ''}
        </span>
        <button type="button" className={styles.closeButton} onClick={end} title="가이드 종료">
          ✕
        </button>
      </div>
      <div key={step} className={styles.body}>
        <GuideProgress current={step} />
        <p className={styles.message}>
          <strong>{STEP_TITLES[step]}</strong> — {STEP_MESSAGES[step]}
        </p>
      </div>
    </div>
  )
}
